import {
    AttachmentMenuAction,
    AttachmentMetadata,
    AttachmentOptions,
    defaultAttachmentMenuAction,
    JumpToNewMessageButtonProps,
    MessageThread,
    MessageThreadProps,
} from '@azure/communication-react';
import { sanitizeMessageAttachmentIds } from '../../utils/attachmentUtils';
import { messageThreadStyles } from '../../styles';
import React, { useCallback, useMemo } from 'react';
import { PcpJumpToNewMessageButton } from './PcpJumpToNewMessageButton';
import { OpenFilled } from '@fluentui/react-icons';
import { ChatFilePreviewDialog } from './Attachments/ChatFilePreviewDialog';
import { isImageFile, parseFileExtension } from '../../utils/fileExtension';
import { pcpTimeFormat } from '../../utils/localizationUtils';
import { useScreenRotatedWhileKeyboardIsOpen } from '../../utils/useScreenRotatedWhileKeyboardIsOpen';


export const PcpMessageThreadWrapper = (props: MessageThreadProps): React.ReactElement => {
    const onRenderJumpToNewMessageButton = React.useCallback(
        (props: JumpToNewMessageButtonProps) => <PcpJumpToNewMessageButton {...props} />,
        [],
    );

    const [open, setOpen] = React.useState(false);
    const [previewAttachment, setPreviewAttachment] = React.useState<AttachmentMetadata | null>(null);

    // Memoized so a fresh object isn't handed to the heavy MessageThread on every chat-event
    // re-render (this wrapper re-renders per incoming message via usePropsFor(MessageThread)).
    // The closure only captures the stable useState setters, so an empty dep array is correct.
    const attachmentOptions: AttachmentOptions = useMemo(
        () => ({
            downloadOptions: {
                actionsForAttachment: (attachment: AttachmentMetadata): AttachmentMenuAction[] => {
                    const extension = parseFileExtension(attachment.name);
                    return isImageFile(attachment.name) || extension === 'pdf'
                        ? [
                              {
                                  name: 'Öffnen',
                                  icon: <OpenFilled />,
                                  onClick: (attachmentMetadata: AttachmentMetadata) => {
                                      setOpen(true);
                                      setPreviewAttachment(attachmentMetadata);
                                      return Promise.resolve();
                                  },
                              },
                          ]
                        : [defaultAttachmentMenuAction];
                },
            },
        }),
        [],
    );

    // Replace unsafe attachment ids before they reach FluentUI's AttachmentCard, which uses
    // them verbatim in a `#attachment-<id>` querySelector (an unsafe id crashes the thread on
    // hover/click). Memoized on `messages` so unchanged rows keep their refs.
    const messages = useMemo(() => sanitizeMessageAttachmentIds(props.messages), [props.messages]);

    const onCloseAttachmentPreview = React.useCallback(() => {
        setOpen(false);
        setPreviewAttachment(null);
    }, []);
    const onDisplayDateTimeString = useCallback((messageDate: Date) => pcpTimeFormat(messageDate), [pcpTimeFormat]);


    useScreenRotatedWhileKeyboardIsOpen(() => {
        // Scroll to the bottom of the chat when the screen is rotated while the keyboard is open.
        // This is for iOS where the keyboard would be over the chat content after rotation.
        setTimeout(() => {
            document.querySelector('#sendbox')?.scrollIntoView({ behavior: 'auto', block: 'end' });

            const chatEl = document.querySelector('.fui-Chat') as HTMLElement | null;
            if (chatEl) {
                chatEl.scrollTop = chatEl.scrollHeight;
            }

            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

            setTimeout(() => {
                chatEl && (chatEl.scrollTop = chatEl.scrollHeight);
            }, 100);
        }, 1000);
    });


    return (
        <>
            <ChatFilePreviewDialog
                open={open}
                attachment={previewAttachment ?? undefined}
                onClose={onCloseAttachmentPreview}
            />

            <MessageThread
                {...props}
                messages={messages}
                onRenderMessage={props.onRenderMessage}
                styles={messageThreadStyles}
                onRenderJumpToNewMessageButton={onRenderJumpToNewMessageButton}
                onDisplayDateTimeString={onDisplayDateTimeString}
                attachmentOptions={attachmentOptions}
            />
        </>
    );
};
